import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Truck, QrCode, CheckCircle, Package, Shield, Lock,
  Loader2, MapPin, ArrowRight, Wifi, RotateCcw, Send
} from 'lucide-react';

export default function DeliveryFlowPage() {
  const [activeTab, setActiveTab] = useState('my-trips');
  const [myTrips, setMyTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Delivery modal
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [scannedPassports, setScannedPassports] = useState([]);
  const [receiverName, setReceiverName] = useState('');
  const [delivering, setDelivering] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState(null);

  // Handoff modal
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffPassport, setHandoffPassport] = useState(null);
  const [handoffTenantId, setHandoffTenantId] = useState('');
  const [handoffDeviceId, setHandoffDeviceId] = useState('');
  const [handingOff, setHandingOff] = useState(false);

  // Device return modal
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnTrip, setReturnTrip] = useState(null);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    fetchData();
     
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [myRes, allRes] = await Promise.all([
        opsApi.getMyDeliveryTrips(),
        opsApi.getAllDeliveryTrips()
      ]);
      setMyTrips(myRes.data || []);
      setAllTrips(allRes.data || []);
    } catch (err) {
      toast.error('Failed to load delivery data');
    } finally {
      setLoading(false);
    }
  };

  // Open delivery modal for a trip
  const openDeliveryModal = (trip) => {
    setSelectedTrip(trip);
    setScannedPassports([]);
    setReceiverName('');
    setDeliveryResult(null);
    setDeliverOpen(true);
  };

  // Toggle passport scan
  const togglePassportScan = async (passport) => {
    if (scannedPassports.find(p => p.id === passport.id)) {
      setScannedPassports(prev => prev.filter(p => p.id !== passport.id));
    } else {
      try {
        await opsApi.scanAtStop({ passport_id: passport.id });
        setScannedPassports(prev => [...prev, passport]);
        toast.success(`Scanned: ${passport.passport_uuid.slice(0, 12)}...`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Scan failed');
      }
    }
  };

  // Mark delivered
  const handleMarkDelivered = async () => {
    if (scannedPassports.length === 0) {
      toast.error('Scan at least one passport QR first');
      return;
    }
    try {
      setDelivering(true);
      const res = await opsApi.markDelivered({
        work_order_id: selectedTrip.id,
        passport_ids: scannedPassports.map(p => p.id),
        receiver_name: receiverName || null
      });
      setDeliveryResult(res.data);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delivery confirmation failed');
    } finally {
      setDelivering(false);
    }
  };

  // Handoff to next tenant
  const handleHandoff = async () => {
    if (!handoffTenantId || !handoffDeviceId) {
      toast.error('Enter next tenant ID and device ID');
      return;
    }
    try {
      setHandingOff(true);
      const res = await opsApi.handoffToTenant({
        passport_id: handoffPassport.id,
        next_tenant_id: handoffTenantId,
        next_device_id: handoffDeviceId
      });
      toast.success(res.data.message);
      setHandoffOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Handoff failed');
    } finally {
      setHandingOff(false);
    }
  };

  // Return device
  const handleReturnDevice = async () => {
    if (!returnTrip?.device_id) return;
    try {
      setReturning(true);
      const res = await opsApi.returnDevice({ device_id: returnTrip.device_id });
      toast.success(res.data.message);
      setReturnOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Return failed');
    } finally {
      setReturning(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const TripCard = ({ trip, showActions = true }) => (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="font-mono font-bold text-slate-900 dark:text-white text-sm">{trip.wo_number}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {trip.device_uid && <><Wifi className="w-3 h-3 inline mr-1" />{trip.device_uid} · </>}
                {trip.cold_packets} cold packets · {formatDate(trip.started_at)}
              </p>
            </div>
          </div>
          <Badge className={
            trip.status === 'COMPLETED' || trip.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            trip.status === 'IN_TRANSIT' || trip.status === 'in_transit' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
            'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          }>
            {trip.status}
          </Badge>
        </div>

        {/* Passports list */}
        {trip.passports && trip.passports.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {trip.passports.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/30 rounded px-3 py-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className={cn("w-3.5 h-3.5", p.status === 'closed' ? "text-green-500" : "text-teal-500")} />
                  <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{p.passport_uuid.slice(0, 12)}...</span>
                  {p.invoice_number && <span className="text-slate-500 text-xs">· {p.invoice_number}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={p.status === 'closed' ? 'bg-green-100 text-green-700 text-xs' : 'bg-teal-100 text-teal-700 text-xs'}>
                    {p.status === 'closed' && <Lock className="w-3 h-3 mr-0.5" />}
                    {p.status}
                  </Badge>
                  {showActions && p.status === 'active' && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-purple-600 hover:text-purple-800" onClick={() => { setHandoffPassport(p); setHandoffOpen(true); }} data-testid={`handoff-${p.id}`}>
                      <Send className="w-3 h-3 mr-1" />Handoff
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* All-trips view with stats bar */}
        {!trip.passports && (
          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mb-3">
            <span className="flex items-center gap-1"><QrCode className="w-3 h-3" />Active: {trip.active_labels || 0}</span>
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Closed: {trip.closed_labels || 0}</span>
          </div>
        )}

        {showActions && (trip.status === 'STARTED' || trip.status === 'started' || trip.status === 'IN_TRANSIT' || trip.status === 'in_transit') && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
            <Button size="sm" onClick={() => openDeliveryModal(trip)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`deliver-${trip.wo_number}`}>
              <CheckCircle className="w-4 h-4 mr-1" />Deliver at Stop
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setReturnTrip(trip); setReturnOpen(true); }} className="border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300" data-testid={`return-${trip.wo_number}`}>
              <RotateCcw className="w-4 h-4 mr-1" />Return Device
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="delivery-flow-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Deliveries</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">Manage delivery trips, scan at stops, handoff to next tenant</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="my-trips" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">My Trips</TabsTrigger>
          <TabsTrigger value="all-trips" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">All Active Trips</TabsTrigger>
        </TabsList>

        <TabsContent value="my-trips" className="mt-4 space-y-3">
          {myTrips.length === 0 ? (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="p-8 text-center text-slate-500">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No active deliveries assigned to you.</p>
                <p className="text-xs mt-1">Trips are assigned from the Dispatcher Activation flow.</p>
              </CardContent>
            </Card>
          ) : (
            myTrips.map(trip => <TripCard key={trip.id} trip={trip} showActions={true} />)
          )}
        </TabsContent>

        <TabsContent value="all-trips" className="mt-4 space-y-3">
          {allTrips.length === 0 ? (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="p-8 text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No active trips for this tenant.</p>
              </CardContent>
            </Card>
          ) : (
            allTrips.map(trip => <TripCard key={trip.id} trip={trip} showActions={false} />)
          )}
        </TabsContent>
      </Tabs>

      {/* Delivery Modal */}
      <Dialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-500" />Deliver at Stop
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedTrip?.wo_number} — Scan each cold packet, then confirm delivery
            </DialogDescription>
          </DialogHeader>

          {deliveryResult ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Delivery Confirmed</h3>
              <p className="text-slate-500 text-sm">{deliveryResult.closed_count} passport(s) closed & SHA-256 sealed</p>
              {deliveryResult.passports?.map((p, idx) => (
                <div key={p.passport_id || p.short_code || p.invoice_number || idx} className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-left text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Invoice</span><span className="text-slate-700 dark:text-slate-300">{p.invoice_number || '—'}</span></div>
                  <div className="flex justify-between mt-1"><span className="text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" />Hash</span><span className="font-mono text-teal-600 text-xs">{p.master_hash?.slice(0, 24)}...</span></div>
                </div>
              ))}
              <Button onClick={() => setDeliverOpen(false)} className="bg-teal-600 hover:bg-teal-700 mt-2">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  Step 4: Scan each Passport QR on the cold packets at this stop.
                </p>

                {selectedTrip?.passports?.filter(p => p.status === 'active').length === 0 ? (
                  <p className="text-center text-slate-500 text-sm py-4">No active passports to deliver on this trip.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedTrip?.passports?.filter(p => p.status === 'active').map(p => {
                      const isScanned = scannedPassports.find(sp => sp.id === p.id);
                      return (
                        <div
                          key={p.id}
                          onClick={() => togglePassportScan(p)}
                          className={cn(
                            "border rounded-lg p-3 cursor-pointer transition-all flex items-center justify-between",
                            isScanned
                              ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20 ring-1 ring-teal-500"
                              : "border-slate-200 dark:border-slate-700 hover:border-teal-300"
                          )}
                          data-testid={`scan-passport-${p.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <QrCode className={cn("w-5 h-5", isScanned ? "text-teal-600" : "text-slate-400")} />
                            <div>
                              <p className="font-mono text-xs text-slate-700 dark:text-slate-300">{p.passport_uuid.slice(0, 16)}...</p>
                              {p.invoice_number && <p className="text-xs text-slate-500">{p.invoice_number}</p>}
                            </div>
                          </div>
                          {isScanned && <CheckCircle className="w-5 h-5 text-teal-500" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Receiver Name (optional)</label>
                <Input
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Person receiving the delivery"
                  className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                  data-testid="receiver-name-input"
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-500">{scannedPassports.length} scanned</span>
                <Button
                  onClick={handleMarkDelivered}
                  disabled={delivering || scannedPassports.length === 0}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="confirm-deliver-btn"
                >
                  {delivering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Mark Delivered & Seal
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Handoff Modal */}
      <Dialog open={handoffOpen} onOpenChange={setHandoffOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-500" />Handoff to Next Tenant
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Transfer custody — passport chain continues under the next Trackotic tenant's device.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
              <p className="text-sm text-purple-800 dark:text-purple-300">
                <Shield className="w-4 h-4 inline mr-1" />
                Passport: {handoffPassport?.passport_uuid?.slice(0, 16)}...
              </p>
              {handoffPassport?.invoice_number && <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">Invoice: {handoffPassport.invoice_number}</p>}
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Next Tenant ID</label>
              <Input value={handoffTenantId} onChange={(e) => setHandoffTenantId(e.target.value)} placeholder="UUID of receiving tenant" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white font-mono text-sm" data-testid="handoff-tenant-input" />
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Next Device ID</label>
              <Input value={handoffDeviceId} onChange={(e) => setHandoffDeviceId(e.target.value)} placeholder="UUID of receiving device" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white font-mono text-sm" data-testid="handoff-device-input" />
            </div>
            <p className="text-xs text-slate-500">Current entry will be closed & sealed. A new entry opens under the next tenant's device. Passport stays OPEN.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoffOpen(false)} className="dark:border-slate-600 dark:text-slate-300">Cancel</Button>
            <Button onClick={handleHandoff} disabled={handingOff} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-handoff-btn">
              {handingOff ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Handoff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Return Modal */}
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-teal-500" />Return Device
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <Wifi className="w-12 h-12 text-teal-500 mx-auto mb-3" />
            <p className="text-slate-700 dark:text-slate-300 font-medium">{returnTrip?.device_uid}</p>
            <p className="text-sm text-slate-500 mt-2">End of day — return device to base. Device will be marked ACTIVE and ready for reuse.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnOpen(false)} className="dark:border-slate-600 dark:text-slate-300">Cancel</Button>
            <Button onClick={handleReturnDevice} disabled={returning} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-return-btn">
              {returning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Return Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
